/** Normalisasi nama desa/kecamatan untuk pencocokan. */
export function normalizeForMatch(str) {
  return String(str || '').trim().toUpperCase();
}

/**
 * Daftar kecamatan unik dari GeoJSON desa (urut label tampilan).
 * @param {GeoJSON.Feature[]} features
 * @returns {{ key: string, label: string }[]}
 */
export function listKecamatanFromGeoFeatures(features) {
  const byKey = new Map();
  for (let i = 0; i < features.length; i++) {
    const f = features[i];
    const raw = f.properties?.KECAMATAN;
    const key = normalizeForMatch(raw);
    if (!key) continue;
    if (!byKey.has(key)) {
      byKey.set(key, String(raw || '').trim() || key);
    }
  }
  return [...byKey.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'id'));
}

/**
 * Perlebar bbox { minLat, maxLat, minLng, maxLng } dari koordinat GeoJSON (lng, lat).
 */
export function extendBboxFromGeometry(geom, bbox) {
  if (!geom || !bbox) return;
  const ex = (lng, lat) => {
    bbox.minLat = Math.min(bbox.minLat, lat);
    bbox.maxLat = Math.max(bbox.maxLat, lat);
    bbox.minLng = Math.min(bbox.minLng, lng);
    bbox.maxLng = Math.max(bbox.maxLng, lng);
  };
  const ring = (r) => {
    for (let i = 0; i < r.length; i++) ex(r[i][0], r[i][1]);
  };
  if (geom.type === 'Polygon') {
    for (let i = 0; i < geom.coordinates.length; i++) ring(geom.coordinates[i]);
  } else if (geom.type === 'MultiPolygon') {
    for (let p = 0; p < geom.coordinates.length; p++) {
      const poly = geom.coordinates[p];
      for (let i = 0; i < poly.length; i++) ring(poly[i]);
    }
  }
}

/**
 * @param {'desa'|'kecamatan'} mapLevel
 * @param {string[]|null} kecamatanIncludedKeys dari pengaturan
 * @param {Set<string>} allKecKeysSet kunci kecamatan dari geometri
 * @returns {{ active: boolean, set: Set<string>|null }} active + set subset (boleh kosong)
 */
export function resolveKecamatanIncludedFilter(mapLevel, kecamatanIncludedKeys, allKecKeysSet) {
  if (mapLevel !== 'kecamatan' || !allKecKeysSet || allKecKeysSet.size === 0) {
    return { active: false, set: null };
  }
  if (kecamatanIncludedKeys == null) return { active: false, set: null };
  if (!Array.isArray(kecamatanIncludedKeys)) return { active: false, set: null };
  const s = new Set(kecamatanIncludedKeys.map((x) => normalizeForMatch(x)));
  if (s.size === 0) return { active: true, set: s };
  let full = true;
  for (const k of allKecKeysSet) {
    if (!s.has(k)) {
      full = false;
      break;
    }
  }
  if (full) return { active: false, set: null };
  return { active: true, set: s };
}

/**
 * Gabungkan polygon desa per kecamatan menjadi satu MultiPolygon per kecamatan.
 * @param {GeoJSON.Feature[]} features fitur desa dengan properties.KECAMATAN
 */
export function mergeFeaturesByKecamatan(features) {
  const groups = new Map();
  for (let i = 0; i < features.length; i++) {
    const f = features[i];
    const rawKec = f.properties?.KECAMATAN || '';
    const k = normalizeForMatch(rawKec);
    if (!k) continue;
    if (!groups.has(k)) {
      groups.set(k, { KECAMATAN: rawKec, polys: [] });
    }
    const g = groups.get(k);
    const geom = f.geometry;
    if (!geom) continue;
    if (geom.type === 'Polygon') {
      g.polys.push(geom.coordinates);
    } else if (geom.type === 'MultiPolygon') {
      for (const poly of geom.coordinates) {
        g.polys.push(poly);
      }
    }
  }
  const out = [];
  groups.forEach((g) => {
    out.push({
      type: 'Feature',
      properties: {
        DESA: '',
        KECAMATAN: g.KECAMATAN,
      },
      geometry: {
        type: 'MultiPolygon',
        coordinates: g.polys,
      },
    });
  });
  return out;
}
